// Manual playback of sound blocks.

import type CreativeEngine from "@cesdk/engine";
import type {DesignBlockId} from "@cesdk/cesdk-js";
import {BLOCK_TRIM_LENGTH_META_KEY, BLOCK_TRIM_OFFSET_META_KEY} from "../constants.ts";
import * as audioContext from "./audio-context.ts";
import {isMobile} from "../utils/browser.ts";
import {getAbsoluteStartOnPage, getDuration} from "../utils/slideUtilities.ts";
import * as virtualizedSlide from "../state/virtualizedSlide.ts";

// testing parameters
const TEST_ALWAYS_USE:boolean = true;
const TEST_NO_MUTE_ORIGINAL:boolean = false;

// state
let willUse: boolean | undefined;   // cached value for shouldUse()

type SoundInfo = {
    id: DesignBlockId;

    // file
    fileUri: string,

    // playback properties
    start: number;
    end: number;
    speed: number;
    volume: number;
    trimOffset: number;
    trimLength: number;

    // load status
    loading: boolean;
    loaded: boolean;
    audioData: AudioBuffer | null;  // TODO: reuse if used by multiple sound blocks
    audioSourceNode: AudioBufferSourceNode | null;
    gainNode?: GainNode | null;

    // playback status
    started: boolean;
    finished: boolean;
    paused: boolean;
};

let isPaused:boolean = false;
let currentSounds: SoundInfo[] = [];

export function isPlaying(): boolean {
    return currentSounds.some(s => s.started && !s.finished && !s.paused);
}

export function shouldUse(): boolean {
    if (willUse !== undefined)
        return willUse;

    if (isMobile() || !supportsWebCodecs()) {
        willUse = true;
        return true;
    }

    if (TEST_ALWAYS_USE) {
        willUse = true;
        return true;
    }

    willUse = false;
    return false;
}

function getSlideSounds(engine: CreativeEngine, slide: virtualizedSlide.VirtualizedSlideInfo)
{
    if (!slide?.scenePageId)
        return;

    // find all sounds - either from the page's audio blocks, or the slide's sound list cache
    const soundIds: DesignBlockId[] = getPageAudioBlocks(engine, slide.scenePageId!);
    let sounds: SoundInfo[] = [];

    if (soundIds.length)
    {
        sounds = soundIds.map((id) =>
        {
            const start = getAbsoluteStartOnPage(engine, id, slide.scenePageId!);
            const dur = getDuration(engine, id);

            let trimOffset = 0;
            let trimLength = 0;
            try {
                if (engine.block.hasMetadata(id, BLOCK_TRIM_OFFSET_META_KEY))
                    trimOffset = Number(engine.block.getMetadata(id, BLOCK_TRIM_OFFSET_META_KEY));
                if (engine.block.hasMetadata(id, BLOCK_TRIM_LENGTH_META_KEY))
                    trimLength = Number(engine.block.getMetadata(id, BLOCK_TRIM_LENGTH_META_KEY));
            }
            catch(err) {
                console.error("Error getting sound block metadata: ", err);
            }

            return {
                id: id,
                fileUri: engine.block.getString(id, "audio/fileURI"),
                start: start,
                end: start + dur,
                speed: engine.block.getPlaybackSpeed(id),
                volume: engine.block.getVolume(id),
                trimOffset: trimOffset,
                trimLength: trimLength,
                loading: false,
                loaded: false,
                audioData: null,
                audioSourceNode: null,
                gainNode: null,
                started: false,
                finished: false,
                paused: false
            };
        }).sort((a, b) => a.start - b.start);

        // save a copy of the sounds in the VirtualizedSlideInfo, in case the slide is re-used (we will have removed the
        // audio blocks to avoid loading by ce.sdk)
        if (sounds.length && !slide.soundCache?.length) {
            slide.soundCache = [];
            for (const s of sounds) {
                slide.soundCache.push({
                    id: s.id,
                    fileUri: s.fileUri,
                    start: s.start,
                    end: s.end,
                    speed: s.speed,
                    volume: s.volume,
                    trimOffset: s.trimOffset,
                    trimLength: s.trimLength,
                });
            }
        }
    }
    else if (slide.soundCache?.length)
    {
        for (const s of slide.soundCache) {
            sounds.push({
                id: s.id,
                fileUri: s.fileUri,
                start: s.start,
                end: s.end,
                speed: s.speed,
                volume: s.volume,
                trimOffset: s.trimOffset,
                trimLength: s.trimLength,
                loading: false,
                loaded: false,
                audioData: null,
                audioSourceNode: null,
                gainNode: null,
                started: false,
                finished: false,
                paused: false
            });
        }
    }

    // remove existing sounds from slide
    if (!TEST_NO_MUTE_ORIGINAL)
    {
        for (const id of soundIds) {
            engine.block.destroy(id);
        }
    }

    currentSounds = sounds;
    return currentSounds;
}

/**
 * Remove all sound blocks from a slide.
 * Used by the player for the first slide (to prevent audio from playing before the first interaction).
 */
export function removeSoundsFromSlide(engine: CreativeEngine, pageId: DesignBlockId) {
    // find all sound blocks
    const soundIds: DesignBlockId[] = getPageAudioBlocks(engine, pageId);

    for (const id of soundIds) {
        engine.block.destroy(id);
    }
}

/**
 * Preload a slide's sounds. Will not return until sounds are finished loading.
 * Will remove sound blocks from the page to prevent double-load.
 * Call when a slide is about to play (but before forceLoadResources() is called).
 */
export async function preloadSounds(engine: CreativeEngine, slide: virtualizedSlide.VirtualizedSlideInfo): Promise<void>
{
    // clean-up any existing state (previous slide)
    unloadSounds();

    // build the sound map
    getSlideSounds(engine, slide);

    if (!currentSounds.length)
        return;

    if (!audioContext.context) {
        console.error("Audio context not available, skipping sound preload");
        return;
    }

    await Promise.all(currentSounds.map(async (sound) => {
        // start loading
        sound.loading = true;
        try {
            const res = await fetch(sound.fileUri);
            if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
            const arrayBuffer = await res.arrayBuffer();
            // decodeAudioData can throw on unsupported/corrupt files
            sound.audioData = await audioContext.context!.decodeAudioData(arrayBuffer);
            sound.loaded = true;
        } catch (err) {
            console.error("Failed to preload sound:", sound.fileUri, err);
            sound.loaded = false;
            sound.audioData = null;
        } finally {
            sound.loading = false;
        }
    }));
}

export function pause() {
    // prevent double call
    if (isPaused)
        return;

    // state
    isPaused = true;

    // pause any playing sounds
    for (const sound of currentSounds)
    {
        if (sound.started && !sound.finished)
        {
            if (sound.audioSourceNode) {
                try {
                    sound.audioSourceNode.stop();
                } catch (e) {
                    // ignore
                }
                try {
                    sound.audioSourceNode.disconnect();
                } catch {}
                sound.audioSourceNode = null;
            }
            if (sound.gainNode) {
                try { sound.gainNode.disconnect(); } catch {}
                sound.gainNode = null;
            }
            sound.paused = true;
        }
    }
}

export function unPause() {
    // prevent double call
    if (!isPaused)
        return;

    // state
    isPaused = false;

    // don't continue if there's no audio context
    if (!audioContext.context)
        return;

    // resume context if we are becoming unpaused
    void audioContext.resume();

    // resume any previously paused sounds
    for (const sound of currentSounds)
    {
        if (sound.started && sound.paused) // don't test for finished here, will be set by the onended callback
        {
            // resume
            if (sound.paused) {
                void playSound(sound);
                sound.paused = false;
            }
        }
    }
}

/**
 * Start ticking for sound playback to track engine playback.
 */
export function startSoundPlayback(engine: CreativeEngine, pageId: DesignBlockId): (() => void) | null {
    engineForResume = engine;
    pageIdForResume = pageId;

    // check for no sounds, no need to start the tracker
    if (!currentSounds.length)
        return null;

    let raf: number = 0;
    let lastTime = -1;
    //const fired = new Set<Id>();

    const tick = () => {
        // get the current playback time
        const curTime = engine.block.supportsPlaybackTime(pageId) ? engine.block.getPlaybackTime(pageId) : 0;

        // If scrubbed backwards (or jumped), reset so hits can fire again.
        //if (lastTime >= 0 && curTime + 1e-6 < lastTime) {
        //    fired.clear();
        //}

        for (const s of currentSounds) {
            // play when crossing each sound start boundary
            if (lastTime < s.start && curTime >= s.start)
            {
                // start playing the sound
                void playSound(s);
            }
        }

        lastTime = curTime;
        raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    // return disposer
    return () => {
        cancelAnimationFrame(raf);
        engineForResume = null;
        pageIdForResume = null;
    };
}

function unloadSounds()
{
    for (const sound of currentSounds) {
        if (sound.audioSourceNode) {
            try {
                sound.audioSourceNode.stop();
            } catch (e) {
                // ignore
            }
            try {
                sound.audioSourceNode.disconnect();
            } catch {}
            sound.audioSourceNode = null;
        }
        if (sound.gainNode) {
            try { sound.gainNode.disconnect(); } catch {}
            sound.gainNode = null;
        }
    }

    currentSounds = [];
}

async function playSound(sound: SoundInfo)
{
    // check for no audio
    if (sound.volume === 0) {
        return
    }

    // create audio context if doesn't exist, should be done on first interaction
    if (!audioContext.context) {
        console.error("No audio context");
        return;
    }

    // iOS may suspend until user gesture
    if (audioContext.context.state === 'suspended') {
        //await audioCtx.resume();
        console.warn("Audio context suspended");
    }

    // create source node
    const source = audioContext.context.createBufferSource();
    sound.audioSourceNode = source;
    source.buffer = sound.audioData;

    if (sound.volume !== 1) {
        const gainNode = audioContext.context.createGain();
        gainNode.gain.value = sound.volume;
        sound.gainNode = gainNode;

        source.connect(gainNode);
        gainNode.connect(audioContext.context.destination);
    } else {
        // Ensure previous node artifacts are cleared
        sound.gainNode = null;
        source.connect(audioContext.context.destination);
    }

    // custom playback speed
    if (sound.speed !== 1)
        source.playbackRate.value = sound.speed;

    // track playback
    source.onended = () => {
        sound.finished = true;
        // free memory / disconnect
        try { source.disconnect(); } catch {}
        sound.audioSourceNode = null;
        if (sound.gainNode) {
            try { sound.gainNode.disconnect(); } catch {}
            sound.gainNode = null;
        }
    };

    // play
    sound.started = true;
    sound.paused = false;
    sound.finished = false;

    // calculate resume offset if it was paused
    let offset = sound.trimOffset ?? 0;
    if (engineForResume && pageIdForResume)
    {
        const curTime = engineForResume.block.supportsPlaybackTime(pageIdForResume) ? engineForResume.block.getPlaybackTime(pageIdForResume) : 0;

        // The sound starts at s.start on the page.
        // If curTime > s.start, we should start at (curTime - s.start) + s.trimOffset
        if (curTime > sound.start) {
            offset = (curTime - sound.start) + sound.trimOffset;
        }
    }

    //console.log("XXX About to start sound: " + audioContext.context?.state);

    // Guard: audio data must be available
    const buffer = sound.audioData;
    if (!buffer) {
        console.warn("No audio data for sound, skipping:", sound.fileUri);
        sound.finished = true;
        return;
    }

    // Clamp offset and duration to buffer range
    const bufferDuration = buffer.duration;
    if (!Number.isFinite(bufferDuration) || bufferDuration <= 0) {
        console.warn("Invalid buffer duration, skipping sound:", sound.fileUri);
        sound.finished = true;
        return;
    }

    // offset cannot be negative or exceed buffer length
    const clampedOffset = Math.max(0, Math.min(offset, bufferDuration));

    // If fully past the end, nothing to play
    if (clampedOffset >= bufferDuration) {
        sound.finished = true;
        return;
    }

    // If trimLength provided, ensure playback respects the remaining trimmed window
    let playLength: number | undefined = undefined;
    if (sound.trimLength && sound.trimLength > 0) {
        // How far into the trimmed region we are starting (0 if before trimOffset)
        const deltaInTrim = Math.max(0, clampedOffset - (sound.trimOffset ?? 0));
        // Remaining time within the author-specified trimmed window
        const remainingTrimmed = Math.max(0, (sound.trimLength ?? 0) - deltaInTrim);
        if (remainingTrimmed === 0) {
            sound.finished = true;
            return;
        }
        // Also cap by actual remaining buffer to avoid overrun errors
        const remainingBuffer = Math.max(0, bufferDuration - clampedOffset);
        playLength = Math.min(remainingTrimmed, remainingBuffer);
        if (playLength === 0) {
            sound.finished = true;
            return;
        }
    }

    source.start(
        0,
        clampedOffset,                 // offset into buffer (seconds)
        playLength                     // (optional) play only this long
    );
}

let engineForResume: CreativeEngine | null = null;
let pageIdForResume: DesignBlockId | null = null;

function supportsWebCodecs() {
    try {
        return !!(
            self.AudioDecoder
        );
    } catch {
        return false;
    }
}

function isDescendantOf(engine: CreativeEngine, node: DesignBlockId, ancestor: DesignBlockId): boolean {
    let cur: DesignBlockId | null = node;
    while (cur != null) {
        if (cur === ancestor)
            return true;
        cur = engine.block.getParent(cur);
    }
    return false;
}

function getPageAudioBlocks(engine: CreativeEngine, pageId: DesignBlockId): DesignBlockId[]
{
    const allAudio: DesignBlockId[] = engine.block.findByType("audio");
    return allAudio.filter((id: DesignBlockId) => isDescendantOf(engine, id, pageId));
}
